/** @jsxImportSource @emotion/react */
import { CSSProperties, useCallback } from "react";
import {
  Background,
  ReactFlow,
  addEdge,
  ConnectionLineType,
  Panel,
  useNodesState,
  useEdgesState,
  Position,
  Node,
  Edge,
  OnConnect,
  Controls,
  MarkerType,
} from "@xyflow/react";
import dagre, { EdgeLabel, GraphLabel, NodeLabel } from "@dagrejs/dagre";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import "./workflow.css";
import { EuiButton, EuiFlexGroup } from "@inscopix/ideas-eui";
const position = { x: 0, y: 0 };
const edgeType = "smoothstep";

export const initialNodes: Node<{ label: string }>[] = [
  {
    id: "1",
    type: "input",
    data: { label: "input" },
    position,
  },
  {
    id: "2",
    data: { label: "node 2" },
    position,
  },
  {
    id: "2a",
    data: { label: "node 2a" },
    position,
  },
  {
    id: "2b",
    data: { label: "node 2b" },
    position,
  },
  {
    id: "2c",
    data: { label: "node 2c" },
    position,
  },
  {
    id: "2d",
    data: { label: "node 2d" },
    position,
  },
  {
    id: "3",
    data: { label: "node 3" },
    position,
  },
  {
    id: "4",
    data: { label: "node 4" },
    position,
  },
  {
    id: "5",
    data: { label: "node 5" },
    position,
  },
  {
    id: "6",
    type: "output",
    data: { label: "output" },
    position,
  },
  { id: "7", type: "output", data: { label: "output" }, position },
];

export const initialEdges = [
  { id: "e12", source: "1", target: "2", type: edgeType, animated: true },
  { id: "e13", source: "1", target: "3", type: edgeType, animated: true },
  { id: "e22a", source: "2", target: "2a", type: edgeType, animated: true },
  { id: "e22b", source: "2", target: "2b", type: edgeType, animated: true },
  { id: "e22c", source: "2", target: "2c", type: edgeType, animated: true },
  { id: "e2c2d", source: "2c", target: "2d", type: edgeType, animated: true },
  { id: "e45", source: "4", target: "5", type: edgeType, animated: true },
  { id: "e56", source: "5", target: "6", type: edgeType, animated: true },
  { id: "e57", source: "5", target: "7", type: edgeType, animated: true },
];

type WorkflowNodeData = { label: string };
type WorkflowNode = Node<WorkflowNodeData>;
type WorkflowEdge = Edge;

/**
 * TODO Why did i have to type cast this
 */
const dagreGraph = new dagre.graphlib.Graph<
  GraphLabel,
  NodeLabel,
  EdgeLabel
>().setDefaultEdgeLabel(() => ({}));

const nodeWidth = 172;
const nodeHeight = 36;

const getLayoutedElements = (
  nodes: WorkflowNode[],
  edges: WorkflowEdge[],
  direction: "TB" | "LR" = "TB",
) => {
  const isHorizontal = direction === "LR";
  dagreGraph.setGraph({ rankdir: direction });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const newNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    const newNode = {
      ...node,
      targetPosition: isHorizontal ? Position.Left : Position.Top,
      sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
      // We are shifting the dagre node position (anchor=center center) to the top left
      // so it matches the React Flow node anchor point (top left).
      position: {
        x: (nodeWithPosition?.x ?? 0) - nodeWidth / 2,
        y: (nodeWithPosition?.y ?? 0) - nodeHeight / 2,
      },
    };

    return newNode;
  });

  return { nodes: newNodes, edges };
};

type OutputPolicy = "KEEP" | "DISCARD";

type GraphNode<T extends Record<string, string>> = Pick<Node<T>, "id" | "data">;
export interface WorkflowDiagramReactFlowProps {
  graphNodes: GraphNode<{ label: string }>[];
  graphInputs: GraphNode<{ label: string }>[];
  graphResults: GraphNode<{
    label: string;
    outputPolicy: OutputPolicy;
  }>[];
  graphEdges: Pick<Edge, "id" | "source" | "target">[];
}

const nodeColors = {
  tool: "#8db802",
  input: "#7ec8fc",
  result: "#e5f101",
};

const resultStyles: Record<OutputPolicy, CSSProperties> = {
  KEEP: {
    backgroundColor: nodeColors.result,
  },
  DISCARD: {
    backgroundColor: "#f7f8d6",
    border: "1px dashed #9ca3af",
    color: "#6b7280",
    opacity: 0.6,
  },
};

export const WorkflowDiagramReactFlow = ({
  graphNodes,
  graphInputs,
  graphResults,
  graphEdges,
}: WorkflowDiagramReactFlowProps) => {
  const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
    [
      ...graphInputs.map((i) => ({
        ...i,
        style: { backgroundColor: nodeColors.input },
        position,
      })),
      ...graphNodes.map((n) => ({
        ...n,
        position,
        style: { backgroundColor: nodeColors.tool },
      })),
      ...graphResults.map((r) => ({
        ...r,
        position,
        style: resultStyles[r.data.outputPolicy ?? "KEEP"],
      })),
    ],
    [
      ...graphEdges.map((e) => ({
        ...e,
        markerEnd: {
          type: MarkerType.Arrow,
          width: 10,
          height: 10,
          color: "#4c4646",
        },
        style: {
          strokeWidth: 1.5,
          stroke: "#4c4646",
        },
      })),
    ],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(layoutedNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(layoutedEdges);

  const onConnect: OnConnect = useCallback(
    (params) =>
      setEdges((eds) =>
        addEdge(
          { ...params, type: ConnectionLineType.SmoothStep, animated: true },
          eds,
        ),
      ),
    [setEdges],
  );

  const onLayout = useCallback(
    (direction: "TB" | "LR") => {
      const { nodes: layoutedNodes, edges: layoutedEdges } =
        getLayoutedElements(nodes, edges, direction);

      setNodes([...layoutedNodes]);
      setEdges([...layoutedEdges]);
    },
    [nodes, edges, setNodes, setEdges],
  );

  return (
    <ReactFlow
      style={{ height: "100%", width: "100%" }}
      nodes={nodes}
      edges={edges}
      // nodeTypes={{
      //   test: ({ children, data, ...props }) => (
      //     <div>{(data as WorkflowNodeData).label}</div>
      //   ),
      // }}
      nodesConnectable={false}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={onConnect}
      connectionLineType={ConnectionLineType.SmoothStep}
      fitView
      fitViewOptions={{ padding: 0.2 }}
      colorMode="light"
    >
      <Controls />
      <Panel position="top-right">
        <EuiFlexGroup>
          <EuiButton onClick={() => onLayout("TB")}>vertical layout</EuiButton>
          <EuiButton onClick={() => onLayout("LR")}>
            horizontal layout
          </EuiButton>
        </EuiFlexGroup>
        {/* <button className="xy-theme__button" onClick={() => onLayout("TB")}>
          vertical layout
        </button>
        <button className="xy-theme__button" onClick={() => onLayout("LR")}>
          horizontal layout
        </button> */}
      </Panel>
      <Background />
    </ReactFlow>
  );
};
